/**
 * Patient prescriptions, with lab/x-ray result uploads.
 *
 * Replaces `renderPatientPrescriptions`, `renderPatientPrescriptionsList`,
 * `openUploadResultModal` and `_hidePatientPrescription`.
 */

import { useState } from 'react';
import {
  Badge,
  Button,
  EmptyState,
  ErrorState,
  Icon,
  Modal,
  PageHeader,
  Panel,
  Spinner,
} from '../../components/common';
import { MedicalFileViewer, ResultsGallery } from '../../components/common/MedicalFileViewer';
import { useAuth } from '../../context/AuthContext';
import { useNotifications } from '../../context/NotificationContext';
import { usePatientPrescriptions, usePrescriptionActions } from '../../hooks/usePrescriptions';
import { collectResultFiles, readFileAsDataUri, compressImage } from '../../domain/files';
import { PrintPrescriptionButton } from '../../components/common/PrintPrescriptionButton';
import { isoToDisplayDate } from '../../domain/dates';

export function PatientPrescriptions() {
  const { patientId } = useAuth();
  const { success, error: toastError } = useNotifications();
  const { prescriptions, loading, error, reload } = usePatientPrescriptions(patientId);
  const { uploadResult } = usePrescriptionActions(null);

  const [viewingFile, setViewingFile] = useState(null);
  const [uploadTarget, setUploadTarget] = useState(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0 || !uploadTarget) return;

    setUploading(true);
    try {
      const encoded = await Promise.all(
        files.map(async (file) => ({
          name: file.name,
          type: file.type,
          // Images are compressed; PDFs and anything else go through as-is.
          data: file.type.startsWith('image/')
            ? await compressImage(file, { maxWidth: 1600, quality: 0.75 })
            : await readFileAsDataUri(file),
        })),
      );

      await uploadResult({
        prescriptionId: uploadTarget.prescriptionId,
        type: uploadTarget.type,
        itemIndex: uploadTarget.itemIndex,
        files: encoded,
      });

      success(`تم رفع ${encoded.length} ملف بنجاح`);
      setUploadTarget(null);
      reload();
    } catch (err) {
      toastError(err.message || 'تعذر رفع النتيجة');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="الروشتات"
        subtitle={`${prescriptions.length} روشتة`}
        icon="file-text"
        actions={
          <Button variant="ghost" icon="refresh-cw" onClick={reload} loading={loading}>
            تحديث
          </Button>
        }
      />

      {error ? (
        <ErrorState error={error} onRetry={reload} />
      ) : loading && prescriptions.length === 0 ? (
        <Spinner label="جاري تحميل الروشتات..." />
      ) : prescriptions.length === 0 ? (
        <Panel>
          <EmptyState
            icon="file-x"
            title="لا توجد روشتات"
            message="ستظهر هنا الروشتات التي يكتبها لك الأطباء بعد الكشف."
          />
        </Panel>
      ) : (
        <div className="space-y-4">
          {prescriptions.map((prescription) => (
            <PrescriptionCard
              key={prescription.id}
              prescription={prescription}
              onViewFile={setViewingFile}
              onUpload={(type, itemIndex) =>
                setUploadTarget({ prescriptionId: prescription.id, type, itemIndex })
              }
            />
          ))}
        </div>
      )}

      <MedicalFileViewer
        open={Boolean(viewingFile)}
        onClose={() => setViewingFile(null)}
        file={viewingFile}
      />

      <Modal
        open={Boolean(uploadTarget)}
        onClose={() => setUploadTarget(null)}
        title="رفع نتيجة الفحص"
        icon="upload-cloud"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm leading-relaxed text-slate-300">
            اختر صورة أو ملف PDF لنتيجة الفحص. يمكنك رفع أكثر من ملف في نفس الوقت.
          </p>
          <input
            type="file"
            accept="image/*,application/pdf"
            multiple
            disabled={uploading}
            onChange={handleUpload}
            className="w-full text-xs text-slate-400 file:mr-3 file:rounded-lg file:border-0 file:bg-slate-800 file:px-3 file:py-2 file:text-xs file:font-bold file:text-slate-200"
          />
          {uploading && <Spinner variant="inline" label="جاري الرفع..." />}
        </div>
      </Modal>
    </div>
  );
}

function PrescriptionCard({ prescription, onViewFile, onUpload }) {
  const [expanded, setExpanded] = useState(false);

  const medicines = prescription.medicines || [];
  const labTests = prescription.labTests || [];
  const xrays = prescription.xrays || [];
  const doctorName = prescription.PrescribingDoctor?.name || prescription.doctorName || 'طبيب';

  return (
    <Panel padded={false}>
      <button
        type="button"
        onClick={() => setExpanded((open) => !open)}
        className="flex w-full items-center gap-4 p-5 text-right"
      >
        <span className="rounded-xl bg-purple-500/10 p-3 text-purple-400">
          <Icon name="file-text" size={22} />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-bold text-white">{doctorName}</span>
          <span className="mt-0.5 block text-xs text-slate-400">
            {isoToDisplayDate(prescription.date) || prescription.date}
            {prescription.clinicName ? ` — ${prescription.clinicName}` : ''}
          </span>
          <span className="mt-1.5 flex flex-wrap gap-1.5">
            {medicines.length > 0 && (
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300" icon="pill">
                {medicines.length} دواء
              </Badge>
            )}
            {labTests.length > 0 && (
              <Badge className="border-amber-500/30 bg-amber-500/10 text-amber-300" icon="flask-conical">
                {labTests.length} تحليل
              </Badge>
            )}
            {xrays.length > 0 && (
              <Badge className="border-indigo-500/30 bg-indigo-500/10 text-indigo-300" icon="scan">
                {xrays.length} أشعة
              </Badge>
            )}
          </span>
        </span>

        <Icon
          name={expanded ? 'chevron-up' : 'chevron-down'}
          size={20}
          className="shrink-0 text-slate-500"
        />
      </button>

      {expanded && (
        <div className="space-y-5 border-t border-white/5 p-5">
          {prescription.diagnosis && (
            <Section title="التشخيص" icon="clipboard-list">
              <p className="text-sm leading-relaxed text-slate-300">{prescription.diagnosis}</p>
            </Section>
          )}

          {medicines.length > 0 && (
            <Section title="الأدوية" icon="pill">
              <ul className="space-y-2">
                {medicines.map((medicine, index) => (
                  <li
                    key={`${medicine.name}-${index}`}
                    className="rounded-xl border border-white/5 bg-white/5 p-3"
                  >
                    <p className="text-sm font-bold text-white">{medicine.name}</p>
                    <p className="mt-0.5 text-xs text-slate-400">
                      {[medicine.dosage, medicine.frequency, medicine.timing, medicine.duration]
                        .filter(Boolean)
                        .join(' — ')}
                    </p>
                    {medicine.notes && (
                      <p className="mt-1 text-[11px] text-slate-500">{medicine.notes}</p>
                    )}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {labTests.length > 0 && (
            <Section title="التحاليل المطلوبة" icon="flask-conical">
              <ul className="space-y-3">
                {labTests.map((test, index) => (
                  <li key={`${test.name}-${index}`} className="rounded-xl border border-white/5 bg-white/5 p-3">
                    <p className="text-sm font-bold text-white">{test.name}</p>
                    {test.notes && <p className="mt-0.5 text-xs text-slate-400">{test.notes}</p>}
                    <ResultsGallery
                      files={collectResultFiles(test)}
                      doctorComment={test.doctorComment}
                      onView={onViewFile}
                      onUpload={() => onUpload('labTest', index)}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {xrays.length > 0 && (
            <Section title="الأشعة المطلوبة" icon="scan">
              <ul className="space-y-3">
                {xrays.map((xray, index) => (
                  <li key={`${xray.name}-${index}`} className="rounded-xl border border-white/5 bg-white/5 p-3">
                    <p className="text-sm font-bold text-white">{xray.name}</p>
                    {xray.notes && <p className="mt-0.5 text-xs text-slate-400">{xray.notes}</p>}
                    <ResultsGallery
                      files={collectResultFiles(xray)}
                      doctorComment={xray.doctorComment}
                      onView={onViewFile}
                      onUpload={() => onUpload('xray', index)}
                    />
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {prescription.notes && (
            <Section title="ملاحظات الطبيب" icon="sticky-note">
              <p className="text-sm leading-relaxed text-slate-300">{prescription.notes}</p>
            </Section>
          )}

          {prescription.followUpDate && (
            <div className="flex items-center gap-2 rounded-xl border border-cyan-500/20 bg-cyan-500/10 p-3 text-sm text-cyan-200">
              <Icon name="calendar-clock" size={16} />
              موعد المتابعة: {isoToDisplayDate(prescription.followUpDate) || prescription.followUpDate}
            </div>
          )}

          {/* The patient could not print or save their own prescription at all
              before this — the only print button in the app was the doctor's. */}
          <PrintPrescriptionButton prescription={prescription} label="طباعة / حفظ PDF" />
        </div>
      )}
    </Panel>
  );
}

function Section({ title, icon, children }) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-2 text-sm font-bold text-slate-200">
        <Icon name={icon} size={15} className="text-cyan-400" />
        {title}
      </h4>
      {children}
    </div>
  );
}

export default PatientPrescriptions;
